/**
 * Product Gallery - Una sola estructura
 * Mobile: Swiper carousel
 * Desktop: Sticky scroll (mismo DOM)
 */
(function () {
  'use strict';

  const GALLERY_SELECTOR = '[id^="ProductGallery-"]';
  const galleryInstances = new Map();
  let variantSubscriberReady = false;

  function initProductGallery(container) {
    if (!container) return;
    if (container.dataset.galleryReady === '1') {
      return galleryInstances.get(container.dataset.sectionId);
    }

    const sectionId = container.dataset.sectionId;
    const mediaCount = parseInt(container.dataset.mediaCount || '1', 10);
    const isMobile = () => window.innerWidth < 750;

    const scrollWrapper = container.querySelector('.product-gallery__scroll-wrapper');
    const swiperEl = container.querySelector('.product-gallery-swiper');
    const track = container.querySelector('[data-gallery-track]');
    const lightbox = container.querySelector('[data-gallery-lightbox]');
    const lightboxMain = lightbox?.querySelector('[data-gallery-lightbox-main]') || null;
    const lightboxThumbsWrap = lightbox?.querySelector('[data-gallery-lightbox-thumbs]') || null;

    // Reubicar el lightbox al final del <body> para evitar stacking contexts
    // que puedan dejarlo por debajo del header u otros overlays.
    if (lightbox && lightbox.parentNode !== document.body) {
      document.body.appendChild(lightbox);
    }

    let swiperInstance = null;
    let lightboxScrollObserver = null;

    function initSwiper() {
      if (!swiperEl || typeof Swiper === 'undefined') return;

      swiperInstance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: mediaCount > 1,
        pagination: {
          el: container.querySelector('.product-gallery__pagination'),
          clickable: true,
        },
        grabCursor: true,
        // Pinch / double-tap zoom en el carrusel móvil (sin abrir lightbox)
        zoom: {
          maxRatio: 3,
          minRatio: 1,
          toggle: true,
        },
      });
    }

    function destroySwiper() {
      if (swiperInstance) {
        swiperInstance.destroy(true, true);
        swiperInstance = null;
      }
    }

    function updateStickyScroll() {
      if (!scrollWrapper || !track || mediaCount <= 1) return;

      const rect = scrollWrapper.getBoundingClientRect();
      const wrapperHeight = scrollWrapper.offsetHeight;
      const sticky = scrollWrapper.querySelector('.product-gallery__sticky');
      const stickyHeight = sticky?.offsetHeight ?? window.innerHeight;
      const firstSlide = track?.querySelector('.product-gallery__slide');
      const slideHeight = firstSlide?.offsetHeight ?? stickyHeight;

      const scrollableHeight = wrapperHeight - stickyHeight;
      if (scrollableHeight <= 0) return;

      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / scrollableHeight));
      const translateY = -progress * (mediaCount - 1) * slideHeight;
      track.style.transform = `translate3d(0, ${translateY}px, 0)`;
    }

    function onResize() {
      if (isMobile()) {
        if (!swiperInstance) initSwiper();
        if (track) track.style.transform = '';
      } else {
        destroySwiper();
        updateStickyScroll();
      }
    }

    function onScroll() {
      if (!isMobile()) updateStickyScroll();
    }

    function goToMediaId(mediaId) {
      if (!mediaId || !track) return;

      const slides = Array.from(track.querySelectorAll('.product-gallery__slide'));
      const targetIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (targetIndex < 0) return;

      if (isMobile() && swiperInstance) {
        const safeIndex = Math.max(0, Math.min(targetIndex, slides.length - 1));
        swiperInstance.slideToLoop ? swiperInstance.slideToLoop(safeIndex) : swiperInstance.slideTo(safeIndex);
        return;
      }

      const targetSlide = slides[targetIndex];
      const sticky = scrollWrapper?.querySelector('.product-gallery__sticky');
      if (!scrollWrapper || !sticky || !targetSlide) return;

      const wrapperHeight = scrollWrapper.offsetHeight;
      const stickyHeight = sticky.offsetHeight || window.innerHeight;
      const scrollableHeight = wrapperHeight - stickyHeight;
      if (scrollableHeight <= 0) return;

      const totalMovable = track.scrollHeight - stickyHeight;
      if (totalMovable <= 0) return;

      const targetOffset = Math.max(0, Math.min(targetSlide.offsetTop, totalMovable));
      const progress = targetOffset / totalMovable;
      const desiredScroll = scrollableHeight * progress;
      const topOffset = scrollWrapper.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: topOffset + desiredScroll,
        behavior: 'smooth',
      });
    }

    function openLightbox(mediaId) {
      if (!lightbox) return;

      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-gallery-lightbox-open');

      requestAnimationFrame(function () {
        scrollLightboxToMediaId(mediaId, false);
        setupLightboxObserver();
      });
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-gallery-lightbox-open');
      teardownLightboxObserver();
    }

    function scrollLightboxToMediaId(mediaId, smooth) {
      if (!lightboxMain || !mediaId) return;
      const target = lightboxMain.querySelector(
        '[data-gallery-lightbox-slide][data-media-id="' + CSS.escape(String(mediaId)) + '"]'
      );
      if (!target) return;

      const behavior = smooth ? 'smooth' : 'auto';
      const top = target.offsetTop;
      lightboxMain.scrollTo({ top: top, behavior: behavior });
      setActiveLightboxThumb(mediaId);
    }

    function setActiveLightboxThumb(mediaId) {
      if (!lightboxThumbsWrap || !mediaId) return;
      const thumbs = lightboxThumbsWrap.querySelectorAll('[data-gallery-lightbox-thumb]');
      let activeEl = null;
      thumbs.forEach(function (thumb) {
        const isActive = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', isActive);
        if (isActive) activeEl = thumb;
      });
      if (activeEl && typeof activeEl.scrollIntoView === 'function') {
        const thumbRect = activeEl.getBoundingClientRect();
        const wrapRect = lightboxThumbsWrap.getBoundingClientRect();
        if (thumbRect.top < wrapRect.top || thumbRect.bottom > wrapRect.bottom) {
          activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }

    function setupLightboxObserver() {
      if (!lightboxMain || typeof IntersectionObserver === 'undefined') return;
      teardownLightboxObserver();

      const slides = lightboxMain.querySelectorAll('[data-gallery-lightbox-slide]');
      if (!slides.length) return;

      lightboxScrollObserver = new IntersectionObserver(
        function (entries) {
          let best = null;
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              if (!best || entry.intersectionRatio > best.intersectionRatio) {
                best = entry;
              }
            }
          });
          if (best) {
            const id = best.target.dataset.mediaId;
            if (id) setActiveLightboxThumb(id);
          }
        },
        {
          root: lightboxMain,
          threshold: [0.3, 0.5, 0.75],
        }
      );

      slides.forEach(function (slide) {
        lightboxScrollObserver.observe(slide);
      });
    }

    function teardownLightboxObserver() {
      if (lightboxScrollObserver) {
        lightboxScrollObserver.disconnect();
        lightboxScrollObserver = null;
      }
    }

    function onSlideClickForLightbox(event) {
      // En móvil el zoom es pinch/doble toque en el slider; el lightbox queda para desktop
      if (isMobile()) return;
      if (
        event.target.closest(
          'video, iframe, model-viewer, button, .product-gallery__video-wrapper, .product-gallery__model-wrapper, a[href]'
        )
      ) {
        return;
      }
      const slide = event.target.closest('.product-gallery__slide');
      if (!slide || !container.contains(slide)) return;
      if (slide.querySelector('.product-gallery__media--placeholder')) return;
      const mediaId =
        slide.dataset.mediaId || slide.querySelector('[data-media-id]')?.dataset.mediaId || null;
      openLightbox(mediaId);
    }

    function onLightboxClick(event) {
      const closeBtn = event.target.closest('[data-gallery-lightbox-close]');
      if (closeBtn) {
        closeLightbox();
        return;
      }
      const thumb = event.target.closest('[data-gallery-lightbox-thumb]');
      if (thumb) {
        const id = thumb.dataset.mediaId;
        if (id) scrollLightboxToMediaId(id, true);
      }
    }

    function onKeydownForLightbox(event) {
      if (event.key === 'Escape' && lightbox && lightbox.getAttribute('aria-hidden') === 'false') {
        closeLightbox();
      }
    }

    if (track) {
      track.addEventListener('click', onSlideClickForLightbox);
    }
    if (lightbox) {
      lightbox.addEventListener('click', onLightboxClick);
      document.addEventListener('keydown', onKeydownForLightbox);
    }

    function replaceWithHtmlGallery(htmlDocument) {
      if (!htmlDocument || !sectionId) return null;
      const incoming = htmlDocument.getElementById('ProductGallery-' + sectionId);
      if (!incoming || !container.parentNode) return null;

      const currentInstance = galleryInstances.get(sectionId);
      currentInstance?.cleanup?.();
      container.replaceWith(incoming);
      return initProductGallery(incoming);
    }

    if (isMobile()) {
      if (typeof Swiper !== 'undefined') {
        initSwiper();
      } else {
        loadSwiper().then(initSwiper);
      }
    } else {
      updateStickyScroll();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    container.dataset.galleryReady = '1';
    const instance = {
      sectionId,
      goToMediaId,
      replaceWithHtmlGallery,
      cleanup: function cleanup() {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        destroySwiper();
        teardownLightboxObserver();
        if (track) {
          track.removeEventListener('click', onSlideClickForLightbox);
        }
        if (lightbox) {
          lightbox.removeEventListener('click', onLightboxClick);
          document.removeEventListener('keydown', onKeydownForLightbox);
          if (lightbox.getAttribute('aria-hidden') === 'false') {
            closeLightbox();
          }
          if (lightbox.parentNode === document.body) {
            lightbox.remove();
          }
        }
        container.dataset.galleryReady = '0';
        galleryInstances.delete(sectionId);
      },
    };
    galleryInstances.set(sectionId, instance);

    return instance;
  }

  function loadSwiper() {
    return new Promise((resolve) => {
      if (typeof Swiper !== 'undefined') {
        resolve();
        return;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      script.onload = resolve;
      document.body.appendChild(script);
    });
  }

  function init() {
    document.querySelectorAll(GALLERY_SELECTOR).forEach(initProductGallery);
    attachVariantChangeSubscription();
  }

  function attachVariantChangeSubscription() {
    if (variantSubscriberReady) return;
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    variantSubscriberReady = true;
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      const variant = event?.data?.variant;
      const sectionId = event?.data?.sectionId;
      const htmlDocument = event?.data?.html;
      const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      if (!sectionId) return;

      const sectionKey = String(sectionId);
      const getFirstMediaId = function () {
        const track = document.querySelector('#ProductGallery-' + sectionKey + ' [data-gallery-track]');
        if (!track) return null;
        const firstSlide = track.querySelector('.product-gallery__slide[data-media-id]:not([data-media-id=""])');
        return firstSlide ? firstSlide.dataset.mediaId : null;
      };
      const instance = galleryInstances.get(sectionKey);
      if (instance && htmlDocument) {
        const newInstance = instance.replaceWithHtmlGallery(htmlDocument);
        const firstMediaId = getFirstMediaId() || mediaId;
        if (firstMediaId) {
          (newInstance || instance).goToMediaId(firstMediaId);
        }
        return;
      }

      const gallery = document.getElementById('ProductGallery-' + sectionKey);
      if (!gallery) return;
      const newInstance = initProductGallery(gallery);
      if (htmlDocument) {
        const replacedInstance = newInstance?.replaceWithHtmlGallery(htmlDocument);
        const firstMediaId = getFirstMediaId() || mediaId;
        if (firstMediaId) (replacedInstance || newInstance)?.goToMediaId(firstMediaId);
        return;
      }
      const firstMediaId = getFirstMediaId() || mediaId;
      if (firstMediaId) newInstance?.goToMediaId(firstMediaId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const gallery = event.target.querySelector(GALLERY_SELECTOR);
    if (gallery) initProductGallery(gallery);
  });
})();
